import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface InvoiceData {
  invoiceNumber: string;
  reservationId: number;
  guest: {
    name: string;
    email?: string;
    phone?: string;
  };
  room?: {
    number: string;
    type: string;
  };
  checkIn: string;
  checkOut: string;
  nights: number;
  roomRate: number;
  subtotal: number;
  tax?: number;
  serviceCharge?: number;
  total: number;
  paid: number;
  outstanding: number;
  items?: Array<{
    description: string;
    quantity: number;
    rate: number;
    amount: number;
  }>;
}

export const generateInvoice = (data: InvoiceData): void => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let yPos = margin;

  // Header
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('HOSPITALITY ERP', margin, yPos);
  
  yPos += 10;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Professional Hotel Management System', margin, yPos);
  
  yPos += 15;
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', pageWidth - margin - 40, yPos);

  // Invoice Details
  yPos += 15;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Invoice #: ${data.invoiceNumber}`, pageWidth - margin - 40, yPos);
  yPos += 5;
  doc.text(`Date: ${new Date().toLocaleDateString()}`, pageWidth - margin - 40, yPos);
  yPos += 5;
  doc.text(`Reservation ID: ${data.reservationId}`, pageWidth - margin - 40, yPos);

  // Guest Information
  yPos += 15;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Bill To:', margin, yPos);
  yPos += 7;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(data.guest.name, margin, yPos);
  if (data.guest.email) {
    yPos += 5;
    doc.text(`Email: ${data.guest.email}`, margin, yPos);
  }
  if (data.guest.phone) {
    yPos += 5;
    doc.text(`Phone: ${data.guest.phone}`, margin, yPos);
  }

  // Reservation Details
  yPos += 15;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Reservation Details:', margin, yPos);
  yPos += 7;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  if (data.room) {
    doc.text(`Room: ${data.room.number} - ${data.room.type}`, margin, yPos);
    yPos += 5;
  }
  doc.text(`Check-in: ${new Date(data.checkIn).toLocaleDateString()}`, margin, yPos);
  yPos += 5;
  doc.text(`Check-out: ${new Date(data.checkOut).toLocaleDateString()}`, margin, yPos);
  yPos += 5;
  doc.text(`Nights: ${data.nights}`, margin, yPos);

  // Items Table
  yPos += 10;
  const tableData = data.items || [
    {
      description: `Room ${data.room?.number || 'N/A'} - ${data.nights} night(s)`,
      quantity: data.nights,
      rate: data.roomRate,
      amount: data.subtotal,
    },
  ];

  autoTable(doc, {
    startY: yPos,
    head: [['Description', 'Quantity', 'Rate', 'Amount']],
    body: tableData.map(item => [
      item.description,
      item.quantity.toString(),
      `$${item.rate.toFixed(2)}`,
      `$${item.amount.toFixed(2)}`,
    ]),
    theme: 'striped',
    headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { halign: 'center', cellWidth: 30 },
      2: { halign: 'right', cellWidth: 30 },
      3: { halign: 'right', cellWidth: 30 },
    },
  });

  const finalY = (doc as any).lastAutoTable.finalY || yPos + 30;

  // Totals
  let totalY = finalY + 10;
  const totalsX = pageWidth - margin - 60;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Subtotal:', totalsX, totalY);
  doc.text(`$${data.subtotal.toFixed(2)}`, pageWidth - margin, totalY, { align: 'right' });

  if (data.tax && data.tax > 0) {
    totalY += 5;
    doc.text('Tax:', totalsX, totalY);
    doc.text(`$${data.tax.toFixed(2)}`, pageWidth - margin, totalY, { align: 'right' });
  }

  if (data.serviceCharge && data.serviceCharge > 0) {
    totalY += 5;
    doc.text('Service Charge:', totalsX, totalY);
    doc.text(`$${data.serviceCharge.toFixed(2)}`, pageWidth - margin, totalY, { align: 'right' });
  }

  totalY += 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Total:', totalsX, totalY);
  doc.text(`$${data.total.toFixed(2)}`, pageWidth - margin, totalY, { align: 'right' });

  totalY += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Paid: $${data.paid.toFixed(2)}`, totalsX, totalY);
  totalY += 5;
  if (data.outstanding > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 0, 0);
    doc.text(`Outstanding: $${data.outstanding.toFixed(2)}`, totalsX, totalY);
    doc.setTextColor(0, 0, 0);
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 128, 0);
    doc.text('Fully Paid', totalsX, totalY);
    doc.setTextColor(0, 0, 0);
  }

  // Footer
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.text(
    'Thank you for your stay! For inquiries, please contact us.',
    pageWidth / 2,
    pageHeight - 15,
    { align: 'center' }
  );

  // Save PDF
  doc.save(`Invoice_${data.invoiceNumber}.pdf`);
};

